import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import useLoginFlow from '~/hooks/use-login-flow';
import { selectAuthenticated } from '~/components/authentication/ducks/selectors';
import { Outlet, useLocation } from 'react-router-dom';
import { useRouter } from '~/utils/navigation';

/** 
 * Usage:

(preferred)

 <Routes>
        <Route index element={<Landing />} />
        <Route path="landing" element={<Landing />} />
        <Route path="home" element={<UnauthenticatedLoginPromptRoute />}>
           <Route index element={<Home />} />
           ...other routes under /home...
        </Route>
        ...
</Routes>

or

 <Routes>
        <Route index element={<Landing />} />
        <Route path="landing" element={<Landing />} />
        <Route
          path="home"
          element={
            <UnauthenticatedLoginPromptRoute>
              <Home />
            </UnauthenticatedLoginPromptRoute>
          }
        />
        ...
</Routes>
*/

const LoginRoute = () => {
	const { initializeLogin } = useLoginFlow();
	const location = useLocation();
	useEffect(() => {
		initializeLogin({ from: location, options: undefined });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	return null;
};

const UnauthenticatedLoginPromptRoute = ({ children }: { children: React.ReactNode }) => {
	const isAuthenticated = useSelector(selectAuthenticated);
	if (!isAuthenticated) return <LoginRoute />;
	return children ?? <Outlet />;
};

/**
 * Redirects to provided url if unauthenticated, otherwise renders as expected
 */
export const UnauthenticatedRedirectRoute = ({
	children,
	redirectUrl
}: {
	children: React.ReactNode;
	redirectUrl: string;
}) => {
	const isAuthenticated = useSelector(selectAuthenticated);
	const router = useRouter();

	useEffect(() => {
		if (!isAuthenticated) {
			router.push(redirectUrl);
		}
	}, [isAuthenticated, redirectUrl, router]);

	if (!isAuthenticated) {
		return null;
	}
	return children ?? <Outlet />;
};

export default UnauthenticatedLoginPromptRoute;
